import React from "react";
import { AlertTriangle, CheckCircle2, Loader2, WifiOff, Info } from "lucide-react";

// Route-level incident banner covering every state:
// loading · feed unavailable (401/403/temporary) · affecting · nearby-only · clear.
// `data` is the incident payload ({ enabled, incidents, summary, reason }) or null while loading.
export default function IncidentWarning({ data, suggestedDeparture }) {
  if (data === null || data === undefined) {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs text-slate-400" data-testid="incident-status-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking live traffic incidents…
      </div>
    );
  }
  if (data.enabled === false) {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs text-slate-400" data-testid="incident-status-unavailable">
        <WifiOff className="w-3.5 h-3.5" /> Traffic incident data temporarily unavailable
      </div>
    );
  }
  const s = data.summary || {};
  const affecting = s.count_affecting || 0;
  const nearby = s.count_nearby || 0;

  if (affecting > 0) {
    const traffic = s.delay_mins || 0;
    const buffer = s.event_buffer_mins || 0;
    const total = s.suggested_earlier_mins || 0;
    return (
      <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 flex items-start gap-2.5 text-sm text-amber-900"
        data-testid="incident-warning">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
        <div>
          <span className="font-bold">
            {affecting} incident{affecting > 1 ? "s" : ""} affecting this run
            {total > 0 ? ` · approximately +${total} min` : ""}
          </span>
          {(traffic > 0 || buffer > 0) && (
            <div className="text-[13px] text-amber-800 mt-0.5" data-testid="incident-breakdown">
              {traffic > 0 && <>Traffic delay: +{traffic} min</>}
              {buffer > 0 && <>{traffic > 0 ? " · " : ""}Event buffer: +{buffer} min (precautionary)</>}
            </div>
          )}
          {(suggestedDeparture || total > 0) && (
            <div className="text-[13px] text-amber-800 mt-0.5">
              Suggested departure adjustment:{" "}
              {suggestedDeparture
                ? <>leave by <b>{suggestedDeparture}</b>{total > 0 ? ` (+${total} min)` : ""}</>
                : <>leave {total} minutes earlier</>}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (nearby > 0) {
    return (
      <div className="mb-3 flex items-center gap-2 text-xs text-slate-500" data-testid="incident-status-nearby">
        <Info className="w-3.5 h-3.5 text-slate-400" /> {nearby} incident{nearby > 1 ? "s" : ""} near the route (not affecting this run)
      </div>
    );
  }
  return (
    <div className="mb-3 flex items-center gap-2 text-xs text-emerald-600" data-testid="incident-status-clear">
      <CheckCircle2 className="w-3.5 h-3.5" /> No live incidents on this route
    </div>
  );
}
